import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PlayArrow as ResumeIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterAlt as LeadFinderIcon,
  Schedule as CampaignsIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Assignment as TasksIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import OutreachLeadFinder from './OutreachLeadFinder';
import OutreachCampaignsPage from './OutreachCampaignsPage';

interface SavedSession {
  id: number;
  session_id: string;
  name: string | null;
  domain_count: number;
  email_count: number;
  new_emails_count?: number;
  saved_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`outreach-tabpanel-${index}`}
      aria-labelledby={`outreach-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const OutreachHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Saved Sessions states (previously the core home page)
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [resumingSessionId, setResumingSessionId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchSavedSessions();
  }, []);

  const fetchSavedSessions = async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const result = await ApiService.getSavedSessions();
      setSavedSessions(result.sessions || []);
    } catch (err: any) {
      console.error('Error fetching saved sessions:', err);
      setSessionsError(err.message || 'Failed to fetch saved sessions.');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleImportFresh = () => {
    navigate('/outreach/upload');
  };

  const handleResumeSession = (sessionId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Resume Session',
      message: 'Resume this session? Saved domains will be available for validation.',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setResumingSessionId(sessionId);
        try {
          const result = await ApiService.resumeSession(sessionId);
          console.log('[Resume Session] Result:', result);
          
          if (result.stage === 'validation') {
            const domainIdsParam = result.resumedDomainIds?.join(',') || '';
            const validationUrl = `/outreach/validation?session=${sessionId}${domainIdsParam ? `&resumedDomainIds=${domainIdsParam}` : ''}`;
            navigate(validationUrl);
          } else {
            // Default to process page
            navigate(`/outreach/process?session=${sessionId}`);
          }
        } catch (err: any) {
          console.error('Error resuming session:', err);
          alert(`Error: ${err.message || 'Failed to resume session'}`);
        } finally {
          setResumingSessionId(null);
          fetchSavedSessions(); // Refresh list
        }
      },
    });
  };

  const handleDeleteSession = (sessionId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Session',
      message: 'Delete this saved session? This will permanently remove all saved domains and emails.',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        try {
          await ApiService.deleteSession(sessionId);
          alert('Session deleted successfully');
          fetchSavedSessions();
        } catch (err: any) {
          console.error('Error deleting session:', err);
          alert(`Error: ${err.message || 'Failed to delete session'}`);
        }
      },
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Outreach Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Query company databases, create grouped outreach lists, and schedule recurring campaign runs.
          </Typography>
        </Box>
          <Button
            variant="outlined"
            startIcon={<TasksIcon />}
            onClick={() => navigate('/outreach/scheduled-tasks')}
            size="medium"
            sx={{ fontWeight: 'bold' }}
          >
            Scheduled Tasks
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleImportFresh}
            size="medium"
            sx={{ fontWeight: 'bold' }}
          >
            Import Fresh Domains
          </Button>
        </Box>

      {/* Quick Resume Section */}
      {!isLoadingSessions && savedSessions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ResumeIcon color="primary" /> Quick Resume
          </Typography>
          <Grid container spacing={2}>
            {savedSessions.slice(0, 3).map((session) => (
              <Grid item xs={12} sm={4} key={session.id}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => handleResumeSession(session.session_id)}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}>
                      {session.name || 'Untitled Session'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Last saved: {formatDate(session.saved_at)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        size="small" 
                        label={`${session.domain_count} Domains`} 
                        sx={{ fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#f0f7ff', color: '#0070f3' }} 
                      />
                      <Chip 
                        size="small" 
                        label={`${session.email_count} Emails`} 
                        sx={{ fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#f6ffed', color: '#52c41a' }} 
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="success"
                      startIcon={<EmailIcon />}
                      sx={{ fontWeight: 'bold', fontSize: '0.7rem', px: 1, flex: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/outreach/email-results?session=${session.session_id}`);
                      }}
                    >
                      VIEW RESULTS
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="primary"
                      endIcon={<ResumeIcon />}
                      sx={{ fontWeight: 'bold', fontSize: '0.7rem', px: 1, flex: 1 }}
                    >
                      RESUME
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tabs Menu */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="outreach workspace tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '42px',
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: '42px',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '0.9rem',
              px: 2.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Tab icon={<LeadFinderIcon fontSize="small" />} label="Lead Finder" id="outreach-tab-0" />
          <Tab icon={<CampaignsIcon fontSize="small" />} label="Campaigns & Scheduler" id="outreach-tab-1" />
          <Tab icon={<HistoryIcon fontSize="small" />} label="Saved Sessions History" id="outreach-tab-2" />
        </Tabs>
      </Box>

      {/* Tab Panels */}

      {/* Tab 1: Lead Finder & Group Scheduler */}
      <TabPanel value={tabValue} index={0}>
        <OutreachLeadFinder />
      </TabPanel>

      {/* Tab 2: Campaigns Card list */}
      <TabPanel value={tabValue} index={1}>
        <OutreachCampaignsPage />
      </TabPanel>

      {/* Tab 3: Saved Staging Sessions list */}
      <TabPanel value={tabValue} index={2}>
        {sessionsError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSessionsError(null)}>
            {sessionsError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #eaeaea',
              boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
            }}>
              <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Total Saved Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                {isLoadingSessions ? <CircularProgress size={20} /> : savedSessions.length}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #eaeaea',
              boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
            }}>
              <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Total Saved Domains
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                {isLoadingSessions ? <CircularProgress size={20} /> : savedSessions.reduce((sum, s) => sum + s.domain_count, 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #eaeaea',
              boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
            }}>
              <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Total Saved Emails
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                {isLoadingSessions ? <CircularProgress size={20} /> : savedSessions.reduce((sum, s) => sum + s.email_count, 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #d1f2e5',
              boxShadow: '0 4px 15px rgba(17, 183, 86, 0.05)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f4fbf7 100%)'
            }}>
              <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 'semibold', color: '#0f7643', mb: 1 }}>
                New Emails Discovered
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#11b756' }}>
                {isLoadingSessions ? <CircularProgress size={20} /> : savedSessions.reduce((sum, s) => sum + (s.new_emails_count || 0), 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>

          {/* Staging Sessions Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #eaeaea', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Staging Sessions History
                </Typography>
                <IconButton onClick={fetchSavedSessions} size="small" color="primary">
                  <RefreshIcon />
                </IconButton>
              </Box>

              {isLoadingSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : savedSessions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No staging sessions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Funnels from Lead Finder or CSV uploads register here.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ borderRadius: '8px', border: '1px solid #eaeaea' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Session Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Session ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Domains</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Emails</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Saved At</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {savedSessions.map((session) => (
                        <TableRow key={session.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                              {session.name || `Session ${session.session_id.substring(0, 8)}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={session.session_id.substring(0, 8)}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 'medium', height: 20, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                            {session.domain_count}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {session.email_count.toLocaleString()}
                              </Typography>
                              {session.new_emails_count !== undefined && session.new_emails_count > 0 && (
                                <Chip
                                  label={`+${session.new_emails_count.toLocaleString()} new`}
                                  size="small"
                                  sx={{
                                    background: 'linear-gradient(135deg, #11b756 0%, #0c8a3c 100%)',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem',
                                    height: '16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell color="text.secondary">
                            {formatDate(session.saved_at)}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="success" 
                                startIcon={<EmailIcon sx={{ fontSize: '1rem !important' }} />}
                                sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: '90px' }}
                                onClick={() => navigate(`/outreach/email-results?session=${session.session_id}`)}
                              >
                                RESULTS
                              </Button>
                              
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="primary" 
                                startIcon={resumingSessionId === session.session_id ? <CircularProgress size={14} /> : <ResumeIcon sx={{ fontSize: '1rem !important' }} />}
                                sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: '90px' }}
                                onClick={() => handleResumeSession(session.session_id)}
                                disabled={resumingSessionId === session.session_id}
                              >
                                RESUME
                              </Button>

                              <Tooltip title="Delete Session">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteSession(session.session_id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Shared Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDialog.onConfirm} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutreachHomePage;
